/**
 * Structured logging utility for EvidenX frontend
 *
 * Replaces ad-hoc console.log/console.error with structured logging that:
 * - Provides consistent context for troubleshooting
 * - Only logs in development mode (except errors)
 * - Formats logs for easy searching and filtering
 * - Extensible for future error tracking service integration
 *
 * Usage:
 *   import { logger } from '@/utils/logger'
 *
 *   logger.debug('ComponentName', 'Fetching data', { id })
 *   logger.info('ComponentName', 'Data loaded successfully', { count: items.length })
 *   logger.warn('ComponentName', 'Deprecated API used', { endpoint })
 *   logger.error('ComponentName', 'Failed to load data', error, { id })
 */

type LogLevel = 'debug' | 'info' | 'warn' | 'error'

interface LogEntry {
  timestamp: string
  level: LogLevel
  context: string
  message: string
  data: unknown | undefined
  error: Error | undefined
}

/**
 * Formats an error for logging, extracting useful information
 */
function formatError(error: unknown): {
  message: string
  stack: string | undefined
  name: string | undefined
} {
  if (error instanceof Error) {
    return {
      name: error.name,
      message: error.message,
      stack: error.stack,
    }
  }
  return { message: String(error), stack: undefined, name: undefined }
}

/**
 * Structured logger class
 */
class Logger {
  private readonly isDev = import.meta.env.DEV

  /**
   * Internal logging method
   */
  private log(
    level: LogLevel,
    context: string,
    message: string,
    data?: unknown,
    error?: Error
  ): void {
    const entry: LogEntry = {
      timestamp: new Date().toISOString(),
      level,
      context,
      message,
      data,
      error,
    }

    const prefix = `[${entry.timestamp}] [${level.toUpperCase()}] [${context}]`

    if (level === 'error') {
      // Always log errors, even in production
      if (error) {
        console.error(prefix, message, formatError(error), data ?? '')
      } else {
        console.error(prefix, message, data ?? '')
      }
      // Future: Send to error tracking service (Sentry, etc.)
    } else if (this.isDev) {
      // Only log debug/info/warn in development
      switch (level) {
        case 'debug':
          console.debug(prefix, message, data ?? '')
          break
        case 'info':
          console.info(prefix, message, data ?? '')
          break
        case 'warn':
          console.warn(prefix, message, data ?? '')
          break
      }
    }
  }

  /**
   * Debug level - detailed information for diagnosing issues
   * Only visible in development mode
   *
   * @param context - Component or module name
   * @param message - What is happening
   * @param data - Optional additional data
   */
  debug(context: string, message: string, data?: unknown): void {
    this.log('debug', context, message, data)
  }

  /**
   * Info level - general operational information
   * Only visible in development mode
   *
   * @param context - Component or module name
   * @param message - What is happening
   * @param data - Optional additional data
   */
  info(context: string, message: string, data?: unknown): void {
    this.log('info', context, message, data)
  }

  /**
   * Warn level - something unexpected but not breaking
   * Only visible in development mode
   *
   * @param context - Component or module name
   * @param message - What is concerning
   * @param data - Optional additional data
   */
  warn(context: string, message: string, data?: unknown): void {
    this.log('warn', context, message, data)
  }

  /**
   * Error level - something failed
   * Always logged, even in production
   *
   * @param context - Component or module name
   * @param message - What failed
   * @param error - The error that occurred
   * @param data - Optional additional context
   */
  error(context: string, message: string, error: unknown, data?: unknown): void {
    this.log(
      'error',
      context,
      message,
      data,
      error instanceof Error ? error : new Error(String(error))
    )
  }
}

/**
 * Singleton logger instance
 */
export const logger = new Logger()
